<script setup lang="ts">
/**
 * Filter-header over main-content.
 *
 * Viser side-titel til venstre + teleport-target (#filter-shell-actions)
 * til højre, hvor individuelle views kan injectere deres egne action-
 * kontroller (view-toggles, format-toggles, fold-icons etc.) via
 * `<Teleport to="#filter-shell-actions" defer>`.
 *
 * Den tidligere filter-drawer-knap er fjernet. Hvis vi senere får brug for
 * en global filter-bar (periode-pickers, preset-pills, sammenlignings-bar
 * fra fase A10), tilføjes den her uden at fjerne action-targetten.
 *
 * ALTID sticky ([D-132], Charlie 2026-07-09 — omgør D-055's naturlige flow
 * og session 30's per-route opt-in): titel + actions er fastlåst i toppen
 * på ALLE sider. `data-sticky-header` bruges af BudgetView's thead-pinning
 * som offset-anker.
 *
 * Per-side opt-out: Hvis route.meta.showHeader === false vises headeren
 * slet ikke (fx på /settings/api-keys hvor siden har sit eget h2-heading).
 *
 * Per D-026: bg-page (så den blender med side-baggrunden) med en bund-border.
 */

import { computed, watchEffect } from 'vue';
import { useRoute } from 'vue-router';

import AiActivityIndicator from './AiActivityIndicator.vue';

const route = useRoute();
const title = computed<string>(() => {
  const meta = route.meta.title;
  return typeof meta === 'string' ? meta : 'Cockpit';
});

/**
 * Vises kun hvis route ikke har eksplicit opt-out (meta.showHeader === false).
 * Default = true så nye routes uden meta-flag stadig får headeren.
 */
const visible = computed<boolean>(() => route.meta.showHeader !== false);

/**
 * Sider UDEN headeren markeres på <html>, så sticky tabelhoveder klæber helt
 * til toppen i stedet for 3,5 rem nede (global.css, .app-uden-header). Før
 * skulle hver side selv huske .table-sticky-head--flush, og Driftsforstyrrelser
 * glemte det: headeren lå 2-3 rækker nede (Charlie 23-10-2026).
 */
watchEffect(() => {
  document.documentElement.classList.toggle('app-uden-header', !visible.value);
});
</script>

<template>
  <header
    v-if="visible"
    class="bg-page print:hidden sticky top-0 z-50"
    data-sticky-header="true"
  >
    <!--
      Header med to teleport-targets:
        - #filter-shell-title (venstre): side-overskrift (h2 + status-pille).
        - #filter-shell-actions (højre): action-kontroller (toggles, ikoner).

      Ingen border-bottom så header-stripen visuelt blender ind i side-
      baggrunden, og kun indholdet (overskrift + actions) signalerer at her
      er en sticky kontrol-zone. Title-variabel beholdes til document.title-
      handling i router.beforeEach.
    -->
    <!--
      MOBIL (Charlie 25-09-2026): under 768 px er der ikke plads til titel og
      kontroller i bredden, og før skubbede kontrollerne hele siden ud over
      telefonens kant (Bookinger målt til 761 px). Nu kan rækken slides vandret
      som footer-menuen, og titlen beholder sin fulde bredde (shrink-0) frem for
      at blive klippet til ingenting. Fra md er alt som før: titlen afkortes,
      kontrollerne står fast til højre. Kontrollerne har ingen absolutte
      popovers, der kunne klippes af overflow (tooltips og toasts ligger i body).
    -->
    <div
      class="flex items-center justify-between h-14 px-6 gap-4 overflow-x-auto scrollbar-none md:overflow-visible"
      data-testid="filter-shell-row"
    >
      <div id="filter-shell-title" class="flex items-baseline gap-2 shrink-0 md:shrink md:min-w-0 overflow-hidden"></div>
      <!-- AI arbejder ([D-439], §95): 3 lysegrå prikker ved titlen, når en
           sprogmodel- eller billedkørsel hører til siden. -->
      <AiActivityIndicator class="mr-auto ml-3 min-w-0" />
      <div id="filter-shell-actions" class="flex items-center gap-2 shrink-0"></div>
    </div>
  </header>
</template>
